import {
  Box,
  Button,
  ColumnLayout,
  Container,
  FileInput,
  FileInputProps,
  Flashbar,
  FlashbarProps,
  FormField,
  Header,
  Input,
  Modal,
  SpaceBetween,
  Spinner,
  SplitPanel,
  TextContent,
} from '@cloudscape-design/components';
import { formatISO } from 'date-fns';
import { useState } from 'react';
import { splitPanelI18nStrings } from 'src/i18n/split-panel';
import { useUpgradeErrorHandler } from 'src/utils/error-handling';

import {
  updateUpgradeAssessment,
  updateUpgradeAssessmentEngineResult,
  useGetUpgradeAssessment,
} from 'api/admin';

import CopyText from 'components/copy-text';
import { UpgradeAssessmentEditor } from './editor';
import {
  ActionButton,
  UpgradeAssessmentBadgeStatus,
  TemporalBadgeStatus,
} from './helpers';
import {
  ValueWithLabel,
  ValueWithLabelEditButton,
} from 'components/value-with-label';
import HeaderItemsTable from './header';
import MissingCoverageTable from '../missing-coverage';
import { UpgradeAssessmentGenerationOutputDocument } from 'api/admin-models';

export function UpgradeAssessmentDetails({
  upgrade: item,
  onChange,
}: {
  upgrade: { orgId: string; accountId: string; upgradeId: string };
  onChange: () => void;
}) {
  const { data, mutate } = useGetUpgradeAssessment(
    item.accountId,
    item.upgradeId,
  );
  const [editorMode, setEditorMode] = useState<'edit' | 'create' | 'hidden'>(
    'hidden',
  );
  const [desiredVersionEditorVisible, setDesiredVersionEditorVisible] =
    useState(false);
  const [currentVersionEditorVisible, setCurrentVersionEditorVisible] =
    useState(false);
  const [upgradeEngineFile, setUpgradeEngineFile] = useState<File[]>([]);
  const [flashbarItems, setFlashbarItems] = useState<
    FlashbarProps.MessageDefinition[]
  >([]);
  const { handleError, showSuccess } = useUpgradeErrorHandler(setFlashbarItems);

  if (data === undefined) {
    return (
      <SplitPanel
        header={`Upgrade: ${item.upgradeId}`}
        i18nStrings={splitPanelI18nStrings}
      >
        <Spinner />
      </SplitPanel>
    );
  }

  const isGamma = import.meta.env.VITE_API_ENDPOINT.includes('chkk.dog');

  return (
    <SplitPanel
      header={`Upgrade: ${data.upgrade_assessment.name}`}
      i18nStrings={splitPanelI18nStrings}
    >
      <DesiredVersionEditor
        accountId={item.accountId}
        upgradeId={item.upgradeId}
        desiredVersion={data.upgrade_assessment.current_version || ''}
        onDismiss={() => {
          void mutate();
          setDesiredVersionEditorVisible(false);
        }}
        visible={desiredVersionEditorVisible}
      />
      <CurrentVersionEditor
        accountId={item.accountId}
        upgradeId={item.upgradeId}
        currentVersion={data.upgrade_assessment.current_version || ''}
        onDismiss={() => {
          void mutate();
          setCurrentVersionEditorVisible(false);
        }}
        visible={currentVersionEditorVisible}
      />
      <UpgradeAssessmentEditor
        accountId={item.accountId}
        upgradeId={item.upgradeId}
        visible={editorMode !== 'hidden'}
        onCancel={() => setEditorMode('hidden')}
        onConfirm={() => {
          setEditorMode('hidden');
          mutate()
            .then(() => onChange())
            .catch((err) => console.log(err));
        }}
      />
      <SpaceBetween size="l">
        {/* Upgrade Details Section */}
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {data.upgrade_assessment.status === 'ready' && (
                    <>
                      {isGamma && item.orgId !== '' && (
                        <Button
                          key="view-in-webapp"
                          onClick={() => {
                            window.open(
                              import.meta.env.VITE_APP_ENDPOINT +
                                `/orgs/${item.orgId}/accounts/${data.account_id}/upgrades/assessments/${data.upgrade_assessment.id}`,
                              '_blank',
                            );
                          }}
                        >
                          View in WebApp
                        </Button>
                      )}
                      <ActionButton
                        key="mark-as-outdated"
                        onConfirm={async () => {
                          await updateUpgradeAssessment(
                            data.account_id,
                            data.upgrade_assessment.id,
                            {
                              status: 'outdated',
                            },
                          );
                          mutate()
                            .then(() => onChange())
                            .catch((err) => console.log(err));
                        }}
                      >
                        Mark As Outdated
                      </ActionButton>
                    </>
                  )}
                  {data.temporal_status && (
                    <>
                      <FileInput
                        onChange={({
                          detail,
                        }: {
                          detail: FileInputProps.ChangeDetail;
                        }) => {
                          setUpgradeEngineFile(detail.value);

                          const fileReader = new FileReader();
                          fileReader.onloadend = async () => {
                            try {
                              const engineResult = JSON.parse(
                                (fileReader.result as string | undefined) || '',
                              );
                              await updateUpgradeAssessmentEngineResult(
                                data.account_id,
                                data.upgrade_assessment.id,
                                engineResult as UpgradeAssessmentGenerationOutputDocument,
                              );
                              await mutate();
                              showSuccess('Engine result updated successfully');
                            } catch (err: unknown) {
                              handleError(err, {
                                context: 'update engine result',
                              });
                            }
                          };
                          fileReader.readAsText(detail.value[0], 'UTF-8');
                        }}
                        value={upgradeEngineFile}
                      >
                        Update Engine Result
                      </FileInput>
                    </>
                  )}
                  {data.upgrade_assessment.status === 'in-progress' && (
                    <ActionButton
                      key="publish-to-customer"
                      onConfirm={async () => {
                        await updateUpgradeAssessment(
                          data.account_id,
                          data.upgrade_assessment.id,
                          {
                            status: 'ready',
                          },
                        );
                        mutate()
                          .then(() => onChange())
                          .catch((err) => console.log(err));
                      }}
                    >
                      Publish to customer
                    </ActionButton>
                  )}
                </SpaceBetween>
              }
            >
              Upgrade Assessment details
            </Header>
          }
        >
          <SpaceBetween direction="vertical" size="m">
            <Flashbar items={flashbarItems} />
            <ColumnLayout columns={3} variant="text-grid">
              <SpaceBetween size="l">
                <ValueWithLabel label="Account ID">
                  <CopyText
                    copyText={data.account_id}
                    copyButtonLabel="Copy Account ID"
                    successText="Account ID copied"
                    errorText="Account ID failed to copy"
                  />
                </ValueWithLabel>
                <ValueWithLabel label="ID">
                  <CopyText
                    copyText={data.upgrade_assessment.id}
                    copyButtonLabel="Copy Upgrade Assessment ID"
                    successText="Upgrade Assessment ID copied"
                    errorText="Upgrade Assessment ID failed to copy"
                  />
                </ValueWithLabel>
              </SpaceBetween>
              <SpaceBetween size="l">
                <ValueWithLabel label="Name">
                  {data.upgrade_assessment.name}
                </ValueWithLabel>
                <ValueWithLabel label="Requested By">
                  {data.upgrade_assessment.requested_by?.name || '-'} (
                  {data.upgrade_assessment.requested_by?.email || '-'})
                </ValueWithLabel>
              </SpaceBetween>
              <SpaceBetween size="l">
                <ValueWithLabel label="Status">
                  <UpgradeAssessmentBadgeStatus
                    value={data.upgrade_assessment.status}
                  />
                </ValueWithLabel>
                <ColumnLayout columns={2} variant="text-grid">
                  <ValueWithLabel label="Created">
                    {formatISO(data.upgrade_assessment.created)}
                  </ValueWithLabel>
                  {data.upgrade_assessment.updated && (
                    <ValueWithLabel label="Last Update">
                      {formatISO(data.upgrade_assessment.updated)}
                    </ValueWithLabel>
                  )}
                </ColumnLayout>
              </SpaceBetween>
            </ColumnLayout>
          </SpaceBetween>
        </Container>

        {/* Creation Details Section */}
        <Container header={<Header variant="h2">Creation Details</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <SpaceBetween size="xs"></SpaceBetween>
            <SpaceBetween size="xs"></SpaceBetween>
            <SpaceBetween size="xs">
              <ValueWithLabel label="Temporal Status">
                <TemporalBadgeStatus value={data.temporal_status} />
              </ValueWithLabel>
            </SpaceBetween>
          </ColumnLayout>
        </Container>

        {/* Resources */}
        <Container header={<Header variant="h2">Resources</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <SpaceBetween size="xs">
              <Box variant="awsui-key-label">Clusters</Box>
              <TextContent>
                {(data.upgrade_assessment.resources.clusters || []).map((c) => (
                  <CopyText
                    key={c.id}
                    copyText={c.id}
                    copyButtonLabel="Copy Cluster ID"
                    successText="Cluster ID copied"
                    errorText="Cluster ID failed to copy"
                  />
                ))}
                {(data.upgrade_assessment.resources.clusters === undefined ||
                  data.upgrade_assessment.resources.clusters.length === 0) && (
                  <p>-</p>
                )}
              </TextContent>
              <ColumnLayout columns={2} variant="text-grid">
                <ValueWithLabelEditButton
                  label="Current Version"
                  onConfirm={() => setCurrentVersionEditorVisible(true)}
                >
                  {data.upgrade_assessment.current_version || '-'}
                </ValueWithLabelEditButton>
                <ValueWithLabelEditButton
                  label="Desired Version"
                  onConfirm={() => setDesiredVersionEditorVisible(true)}
                >
                  {data.upgrade_assessment.desired_version || '-'}
                </ValueWithLabelEditButton>
              </ColumnLayout>
            </SpaceBetween>
            <SpaceBetween size="xs"></SpaceBetween>
            <SpaceBetween size="xs"></SpaceBetween>
          </ColumnLayout>
        </Container>

        {/* Missing Coverage */}
        <Container header={<Header variant="h2">Missing Coverage</Header>}>
          <MissingCoverageTable
            missingCoverage={data?.missing_coverage || []}
          />
        </Container>

        {/* Headings Info bar */}
        <HeaderItemsTable
          editable={false}
          initialItems={data.upgrade_assessment.header?.items || []}
          onSave={async (newItems) => {
            try {
              await updateUpgradeAssessment(
                data.account_id,
                data.upgrade_assessment.id,
                {
                  header: { items: newItems },
                },
              );
              await mutate();
              showSuccess('Header items updated successfully');
            } catch (err: unknown) {
              handleError(err, {
                context: 'update header items',
              });
            }
          }}
        />
      </SpaceBetween>
    </SplitPanel>
  );
}

function ProcessingDialogModal(props: {
  header: string;
  onConfirm: () => Promise<void>;
  onDismiss: () => void;
  isError?: boolean;
  visible: boolean;
  children: React.ReactNode;
}): JSX.Element {
  const [processing, setProcessing] = useState(false);

  const onConfirmClick = async () => {
    setProcessing(true);
    try {
      await props.onConfirm();
    } catch (err: unknown) {
      console.error(err);
    }
    setProcessing(false);
    props.onDismiss();
  };

  return (
    <Modal
      header={props.header}
      onDismiss={props.onDismiss}
      visible={props.visible}
    >
      <SpaceBetween direction="vertical" size="m">
        {props.children}
        <SpaceBetween direction="horizontal" size="xs">
          <ActionButton
            key="confirm"
            onConfirm={onConfirmClick}
            disabled={processing || props.isError}
          >
            Confirm
          </ActionButton>
          <ActionButton key="cancel" onClick={props.onDismiss}>
            Cancel
          </ActionButton>
        </SpaceBetween>
      </SpaceBetween>
    </Modal>
  );
}

function DesiredVersionEditor(props: {
  accountId: string;
  upgradeId: string;
  desiredVersion: string;
  onDismiss: () => void;
  visible: boolean;
}): JSX.Element {
  const [desiredVersion, setDesiredVersion] = useState(props.desiredVersion);

  return (
    <ProcessingDialogModal
      header="Change Desired Version"
      onConfirm={async () => {
        await updateUpgradeAssessment(props.accountId, props.upgradeId, {
          desired_version: desiredVersion,
        });
      }}
      onDismiss={() => {
        if (props.desiredVersion === desiredVersion) {
          setDesiredVersion(props.desiredVersion);
        }
        props.onDismiss();
      }}
      visible={props.visible}
    >
      <FormField label="Desired Upgrade Version">
        <Input
          value={desiredVersion}
          onChange={({ detail }) => {
            setDesiredVersion(detail.value);
          }}
        />
      </FormField>
    </ProcessingDialogModal>
  );
}
function CurrentVersionEditor(props: {
  accountId: string;
  upgradeId: string;
  currentVersion: string;
  onDismiss: () => void;
  visible: boolean;
}): JSX.Element {
  const [currentVersion, setCurrentVersion] = useState(props.currentVersion);

  return (
    <ProcessingDialogModal
      header="Change CurrentVersion"
      onConfirm={async () => {
        await updateUpgradeAssessment(props.accountId, props.upgradeId, {
          current_version: currentVersion,
        });
      }}
      onDismiss={() => {
        if (props.currentVersion === currentVersion) {
          setCurrentVersion(props.currentVersion);
        }
        props.onDismiss();
      }}
      visible={props.visible}
    >
      <FormField label="Current Upgrade Version">
        <Input
          value={currentVersion}
          onChange={({ detail }) => {
            setCurrentVersion(detail.value);
          }}
        />
      </FormField>
    </ProcessingDialogModal>
  );
}
